// Answer identity and correctness — the ONE implementation.
//
// `advance()` deliberately never holds card content: it walks the GRAPH and
// takes `correct` from its caller, because the comparison needs the materialised
// input block. That left the comparison itself homeless, and the result was a
// hole rather than a duplicate — NOTHING computed correctness. `correct_value`
// was authored in the block editor and `SessionState.correct` was typed, but no
// caller ever populated it, so every `session.correct.<key>` fact was ABSENT at
// runtime and any conditional routing on one silently fell through to
// `otherwise`. Both halves looked right on their own.
//
// This module is that home: the web player, the v1 runtime endpoint, and the
// Swift mirror on iOS all compare through here, so "correct" means one thing on
// every platform.
/** Narrowing helper so callers don't re-implement the type test. */
export function isInputBlock(block) {
    return block.type === 'input';
}
/**
 * The key that answers, attempts and correctness land under.
 *
 * `answer_key ?? block.id` is the D1 wire contract (2026-07-27 format review):
 * facts are written to `session.answers.<key>` / `session.attempts.<key>` /
 * `session.correct.<key>`, and rules route on exactly those paths.
 */
export function effectiveAnswerKey(block) {
    return block.answer_key ?? block.id;
}
/**
 * Whether `value` is the correct answer to `block`.
 *
 * Returns `null` when the block declares no correct answer — an ungraded input.
 * Callers MUST pass `undefined` (not `false`) to `advance` in that case, so the
 * fact stays absent: recording `false` would make "answered wrong" and "this was
 * never a quiz" indistinguishable to a rule, and `session.correct.x eq 'false'`
 * would start matching every ungraded input in the workflow.
 *
 * Comparison rules, normative for the Swift mirror:
 *  - the submitted value is trimmed before every comparison
 *  - `multiple_choice`: exact match against the authored option string, because
 *    the value IS one of `input_options` — case-folding here would let two
 *    options that differ only in case collide
 *  - `text`: trimmed, case-insensitive, whitespace-collapsed
 *  - `number`: numeric comparison, so "3" and "3.0" agree
 *  - `boolean`: truthiness-normalised, so "true"/"1" agree
 *  - `correct_value` as an ARRAY means "any of these" (multi-answer authoring)
 */
export function isAnswerCorrect(block, value) {
    const expected = block.correct_value;
    if (expected === null || expected === undefined)
        return null;
    const candidates = Array.isArray(expected) ? expected : [expected];
    if (candidates.length === 0)
        return null;
    const submitted = value.trim();
    return candidates.some((candidate) => compare(block, candidate, submitted));
}
function compare(block, expected, submitted) {
    const target = expected.trim();
    switch (block.input_type) {
        case 'multiple_choice':
            return target === submitted;
        case 'number': {
            const a = Number(target);
            const b = Number(submitted);
            // Fall back to string equality when either side isn't a number, rather
            // than declaring NaN === NaN correct.
            if (!Number.isFinite(a) || !Number.isFinite(b))
                return target === submitted;
            return a === b;
        }
        case 'boolean':
            return normaliseBoolean(target) === normaliseBoolean(submitted);
        case 'text':
            return normaliseText(target) === normaliseText(submitted);
    }
}
function normaliseText(value) {
    return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}
function normaliseBoolean(value) {
    const v = value.trim().toLocaleLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
}
/**
 * Resolve the answer for a card the learner just responded to.
 *
 * Returns the `{answer_key, value, correct}` triple `advance()` expects, with
 * `correct` OMITTED for ungraded inputs (see `isAnswerCorrect`). Returns null
 * when the card carries no input block — a read-only card advances with no
 * answer at all.
 */
export function buildAnswer(blocks, value) {
    const input = blocks.find(isInputBlock);
    if (!input)
        return null;
    const correct = isAnswerCorrect(input, value);
    return {
        answer_key: effectiveAnswerKey(input),
        value,
        ...(correct === null ? {} : { correct }),
    };
}
